import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Oval } from "react-loader-spinner";

import { setIsLoggedIn } from "../redux/slices/generalSlice";
import notify from "../components/common/notify";
import Container from "../components/common/Container";
import useLocalStorage from "../hooks/useLocalStorage";

/**
 * AuthCallback Page
 * 
 * Handles the OAuth callback from social providers (Google, GitHub, ORCID).
 * Processes the token, stores it, and redirects the user.
 */
const AuthCallback = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const { updateValue: setToken } = useLocalStorage("token");
  const [status, setStatus] = useState("processing"); // processing, success, error
  const [errorMessage, setErrorMessage] = useState("");
  const [errorDetails, setErrorDetails] = useState(null);
  const [hasProcessed, setHasProcessed] = useState(false);

  useEffect(() => {
    // Prevent multiple executions in StrictMode or re-renders
    if (hasProcessed) {
      return;
    }

    const processCallback = async () => {
      const token = searchParams.get("token");
      const role = searchParams.get("role");
      const isNew = searchParams.get("isNew") === "true";
      const profileStep = parseInt(searchParams.get("profileStep") || "0", 10);
      const needsProfileSetup = searchParams.get("needsProfileSetup") === "true";
      const authProvider = searchParams.get("authProvider") || "oauth";
      const redirect = searchParams.get("redirect") || "/";
      const error = searchParams.get("error");
      const message = searchParams.get("message");
      const retryable = searchParams.get("retryable") === "true";
      
      // Profile is completed if profile_step >= 1
      const profileCompleted = profileStep >= 1;

      if (error) {
        setStatus("error");
        const { title, description } = getErrorDetails(error, message);
        setErrorMessage(title);
        setErrorDetails({
          description,
          retryable,
          originalError: error,
        });
        setHasProcessed(true);
        return;
      }

      if (token) {
        try {
          // Store token and auth info
          setToken(token);
          localStorage.setItem("role", role);
          localStorage.setItem("auth_provider", authProvider);
          
          // Set logged in state
          dispatch(setIsLoggedIn(true));

          // Show appropriate message
          if (isNew) {
            notify("Welcome to FunDeSci! Please complete your profile to continue.", "success");
          } else if (!profileCompleted) {
            notify("Please complete your profile to continue.", "info");
          } else {
            notify("Welcome back! You have been logged in.", "success");
          }

          setStatus("success");
          setHasProcessed(true);

          // Redirect after short delay
          setTimeout(() => {
            if (!profileCompleted) {
              // Users with incomplete profiles must complete their profile first
              navigate("/create-profile?social=true");
            } else if (redirect.startsWith("http://") || redirect.startsWith("https://")) {
              // Absolute URL (e.g. https://fundesci.com/telegram/callback?state=...)
              // React Router's navigate() cannot handle absolute URLs — use location.href.
              window.location.href = redirect;
            } else {
              navigate(redirect);
            }
          }, 1500);
        } catch (err) {
          setStatus("error");
          setErrorMessage("Failed to process login. Please try again.");
          setHasProcessed(true);
        }
      } else {
        setStatus("error");
        setErrorMessage("No authentication token received. Please try again.");
        setHasProcessed(true);
      }
    };

    processCallback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getErrorDetails = (errorCode, message) => {
    const errorMap = {
      invalid_state: {
        title: "Security Verification Failed",
        description: "The security token expired or was invalid. This can happen if you took too long to complete the sign-in or opened multiple tabs.",
      },
      invalid_provider: {
        title: "Invalid Authentication Provider",
        description: "The selected authentication provider is not supported.",
      },
      token_exchange_failed: {
        title: "Authentication Failed",
        description: "We couldn't verify your identity with the provider. Please try again.",
      },
      access_denied: {
        title: "Access Denied",
        description: "You denied access to your account. Please try again and grant the required permissions.",
      },
      temporary_error: {
        title: "Temporary Service Issue",
        description: message || "The authentication service is temporarily unavailable. Please try again in a few moments.",
      },
      auth_failed: {
        title: "Authentication Failed",
        description: message || "We couldn't complete your sign-in. Please try again.",
      },
      unexpected_error: {
        title: "Unexpected Error",
        description: message || "An unexpected error occurred. Please try again.",
      },
    };

    return errorMap[errorCode] || {
      title: "Authentication Error",
      description: message || `An error occurred: ${errorCode}`,
    };
  };

  const handleRetry = () => {
    // Navigate back to home or login page to retry
    navigate("/");
  };

  return (
    <Container className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        {status === "processing" && (
          <>
            <Oval
              height={64}
              width={64}
              color="#792CF3"
              secondaryColor="#e5e7eb"
              strokeWidth={4}
              wrapperClass="justify-center mb-6"
            />
            <h2 className="text-2xl font-bold text-secondary mb-2">
              Completing Sign In...
            </h2>
            <p className="text-gray-500">
              Please wait while we set up your account.
            </p>
          </>
        )}

        {status === "success" && (
          <>
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-100 flex items-center justify-center">
              <svg className="w-10 h-10 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-secondary mb-2">
              Success!
            </h2>
            <p className="text-gray-500">
              Redirecting you now...
            </p>
          </>
        )}

        {status === "error" && (
          <>
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-100 flex items-center justify-center">
              <svg className="w-10 h-10 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-secondary mb-2">
              {errorMessage}
            </h2>
            {errorDetails && (
              <p className="text-gray-500 mb-2 max-w-md mx-auto">
                {errorDetails.description}
              </p>
            )}
            {errorDetails?.retryable && (
              <p className="text-sm text-blue-500 mb-4">
                This is a temporary issue. Please try again.
              </p>
            )}
            <div className="flex gap-3 justify-center mt-6">
              {errorDetails?.retryable && (
                <button
                  onClick={handleRetry}
                  className="px-6 py-3 rounded-xl font-semibold text-white bg-gradient-to-r from-primary via-[#8a5cff] to-[#7e7cff] hover:shadow-lg hover:shadow-primary/30 transition-all duration-300"
                >
                  Try Again
                </button>
              )}
              <button
                onClick={() => navigate("/")}
                className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${
                  errorDetails?.retryable
                    ? "text-gray-600 border border-gray-300 hover:bg-gray-50"
                    : "text-white bg-gradient-to-r from-primary via-[#8a5cff] to-[#7e7cff] hover:shadow-lg hover:shadow-primary/30"
                }`}
              >
                Go to Home
              </button>
            </div>
          </>
        )}
      </div>
    </Container>
  );
};

export default AuthCallback;
